// # Card · Badge · Avatar — 보여 주기만 하는 프리미티브(docs/design.md)
//
// - `Card` 는 판 하나다. 테두리·반경·그림자를 토큰으로 맞춘다. 머리(`CardHeader`)는 제목·설명·단추
//   자리를 한 줄에 둔다. 제목 단계는 부르는 쪽이 정한다(`level`) — 판은 자기가 문서의 어디에
//   앉는지 모른다.
// - `Badge` 는 짧은 표식이다. 색(`tone`)과 무게(`variant`)가 직교한다. 숫자 배지는 `count` 로 —
//   0 이하면 **아무것도 안 그린다**(빈 알약이 "안 읽음이 있다" 로 읽힌다), 99 를 넘으면 `99+`.
// - `Avatar` 는 그림이 있으면 그림, 없거나 못 받으면 이니셜이다. 바탕색은 이름에서 **결정적으로**
//   고른다 — 같은 사람은 어느 화면에서도 같은 색이어야 알아본다. 색은 인라인 style 이 아니라
//   `data-hue`(0‥7)로 CSS 가 칠한다. 초록불은 색만으로 말하지 않는다: 이름에 상태를 붙여 읽힌다.
//   사람 번호(`user`)를 주면 얼굴 사진을 `FaceSource` 에서 스스로 찾는다 — 화면마다 사진 주소를
//   손으로 나르지 않는다. 출처가 없거나(미리보기·시험) 사진이 없으면 이니셜이다.

import {
  createContext,
  type HTMLAttributes,
  type ReactNode,
  use,
  useEffect,
  useState,
  useSyncExternalStore,
} from 'react'
import { useT } from '../i18n.ts'
import { cx } from './cx.ts'

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** 안쪽 여백. 기본 `md`(16px). 목록을 꽉 채워 담을 때 `none`. */
  padding?: 'none' | 'sm' | 'md' | 'lg'
  /** 참이면 누를 수 있는 판처럼 올렸을 때 떠오른다. 누름 자체는 안에 둔 단추·링크가 받는다. */
  interactive?: boolean
}

export function Card(props: CardProps) {
  const { padding, interactive, className, ...rest } = props
  return (
    <div
      {...rest}
      className={cx('Card', className)}
      data-padding={padding ?? 'md'}
      data-interactive={interactive || undefined}
    />
  )
}

export interface CardHeaderProps {
  title: ReactNode
  /** 제목 밑 한 줄. */
  description?: ReactNode
  /** 오른쪽 끝 단추 자리. */
  actions?: ReactNode
  /** 제목 단계. 기본 3. */
  level?: 2 | 3 | 4
  className?: string
}

export function CardHeader(props: CardHeaderProps) {
  const level = props.level ?? 3
  const Heading = level === 2 ? 'h2' : level === 4 ? 'h4' : 'h3'
  return (
    <div className={cx('CardHeader', props.className)}>
      <div className="CardHeader-text">
        <Heading className="CardHeader-title">{props.title}</Heading>
        {props.description && <p className="CardHeader-description">{props.description}</p>}
      </div>
      {props.actions && <div className="CardHeader-actions">{props.actions}</div>}
    </div>
  )
}

export type BadgeTone = 'neutral' | 'accent' | 'danger' | 'ok' | 'warn'
export type BadgeVariant = 'soft' | 'solid' | 'outline'

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** 기본 `neutral`. */
  tone?: BadgeTone
  /** 기본 `soft`. 숫자 배지는 기본 `solid`. */
  variant?: BadgeVariant
  /** 숫자 배지. 0 이하면 그리지 않는다. */
  count?: number
  /** 숫자 상한. 기본 99. */
  max?: number
}

/** 숫자 배지의 글자. 넘치면 `99+`. 0 이하는 `null`. */
export function count_label(count: number, max?: number): string | null {
  if (!(count > 0)) return null
  const top = max ?? 99
  return count > top ? `${top}+` : String(Math.floor(count))
}

export function Badge(props: BadgeProps) {
  const { tone, variant, count, max, className, children, ...rest } = props
  const numeric = count !== undefined
  const text = numeric ? count_label(count, max) : null
  if (numeric && text === null) return null
  return (
    <span
      {...rest}
      className={cx('Badge', className)}
      data-tone={tone ?? 'neutral'}
      data-variant={variant ?? (numeric ? 'solid' : 'soft')}
      data-count={numeric || undefined}
    >
      {numeric ? text : children}
    </span>
  )
}

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

export interface AvatarProps {
  /** 사람(또는 봇·채널) 이름. 이니셜과 색, 읽을 이름이 여기서 나온다. */
  name: string
  /** 그림 주소. 못 받으면 이니셜로 돌아간다. 주면 `user` 보다 앞선다. */
  src?: string | null
  /** 사람 번호. 주면 `FaceSource` 에서 그 사람의 얼굴 사진을 찾는다. */
  user?: number
  /** 기본 `md`(28px). xs 16 · sm 20 · lg 36 · xl 64. */
  size?: AvatarSize
  /** 초록불. 없으면 안 그린다. */
  presence?: 'on' | 'off'
  /** 기본 `square`(둥근 네모, 슬랙과 같다). */
  shape?: 'square' | 'circle'
  /** 참이면 읽을 이름을 싣지 않는다 — 바로 옆에 이름 글자가 있을 때. */
  decorative?: boolean
  className?: string
}

const HUES = 8

/**
 * 얼굴 사진의 출처. 클라이언트의 묶음 조회(`Lookup`)와 같은 모양이다 — ui 는 `@maru/client` 를
 * 모르므로 모양만 적는다. 열쇠는 사람 번호의 글자, 값이 `null` 이면 사진이 없다.
 */
export interface FaceSource {
  subscribe(listen: () => void): () => void
  slice(): ReadonlyMap<string, string | null>
  need(keys: readonly string[]): void
}

export const FaceSources = createContext<FaceSource | null>(null)

const NO_FACES: ReadonlyMap<string, string | null> = new Map()
const no_subscribe = () => () => {}
const no_faces = () => NO_FACES

/** 이름 → 색 번호(0‥7). 같은 이름은 언제나 같은 색이다. */
export function avatar_hue(name: string): number {
  // FNV-1a 에 murmur3 끝섞기 — 곱하기 31 만으로는 한글처럼 이웃한 코드값이 몇 칸에 몰렸다.
  let h = 0x811c9dc5
  for (const ch of name) h = Math.imul(h ^ (ch.codePointAt(0) ?? 0), 0x01000193)
  h ^= h >>> 16
  h = Math.imul(h, 0x85ebca6b)
  h ^= h >>> 13
  h = Math.imul(h, 0xc2b2ae35)
  h ^= h >>> 16
  return (h >>> 0) % HUES
}

const HANGUL = /^[가-힣]+$/

/**
 * 이름 → 이니셜. 한글 이름은 성을 떼고 뒤 두 글자(`김가람` → `가람`) — 한국 메신저가 늘 그렇게
 * 부른다. 로마자는 앞 두 낱말의 첫 글자를 대문자로. 비면 `?`.
 */
export function initials(name: string): string {
  const clean = name.trim()
  if (clean === '') return '?'
  const words = clean.split(/\s+/)
  const first = words[0] ?? ''
  if (words.length === 1 && HANGUL.test(first)) {
    const chars = [...first]
    return chars.length >= 3 ? chars.slice(-2).join('') : chars.join('')
  }
  const letters = words
    .slice(0, 2)
    .map((w) => [...w][0] ?? '')
    .join('')
  return letters.toUpperCase()
}

/** 읽을 이름 — 초록불은 색만으로 말하지 않는다. */
function spoken(name: string, presence: AvatarProps['presence'], t: (ko: string) => string) {
  if (presence === 'on') return `${name} (${t('접속 중')})`
  if (presence === 'off') return `${name} (${t('자리 비움')})`
  return name
}

export function Avatar(props: AvatarProps) {
  const t = useT()
  const [broken, set_broken] = useState<string | null>(null)
  const faces = use(FaceSources)
  const found = useSyncExternalStore(
    faces?.subscribe ?? no_subscribe,
    faces?.slice ?? no_faces,
    faces?.slice ?? no_faces,
  )
  const who = props.user
  useEffect(() => {
    if (faces !== null && who !== undefined && props.src == null) faces.need([String(who)])
  }, [faces, who, props.src])
  const src = props.src ?? (who === undefined ? null : (found.get(String(who)) ?? null))
  const show = src !== null && broken !== src
  const size = props.size ?? 'md'
  // 16px 에 두 글자는 읽히지 않는다 — 한 글자만.
  const all = initials(props.name)
  const letters = size === 'xs' ? ([...all][0] ?? '?') : all
  const reading = props.decorative
    ? { 'aria-hidden': true }
    : { role: 'img', 'aria-label': spoken(props.name, props.presence, t) }
  return (
    <span
      {...reading}
      className={cx('Avatar', props.className)}
      data-size={size}
      data-shape={props.shape ?? 'square'}
      data-hue={avatar_hue(props.name)}
    >
      {show ? (
        <img
          className="Avatar-image"
          src={src}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => set_broken(src)}
        />
      ) : (
        <span
          className="Avatar-initials"
          data-hangul={HANGUL.test(letters) || undefined}
          aria-hidden
        >
          {letters}
        </span>
      )}
      {props.presence && <span className="Avatar-presence" data-presence={props.presence} />}
    </span>
  )
}
